import Logger from '../lib/logger';
import event from '../lib/mixins/event';
import { CountUp } from 'countup.js';
import type { EventEmitter } from '../lib/mixins/event';

/** Object.assign below grafts the event mixin onto the prototype at runtime. */
interface Counter extends EventEmitter {}

class Counter {

	// Vars
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	NAME: string;
	dom: HTMLElement;
	number: HTMLElement | null;
	from?: string;
	to?: string;
	duration?: string;
	activeClass: string;
	tickerStarted?: boolean;

	/**
	 * Class constructor
	 */
	constructor(dom: HTMLElement) {

		this.NAME = 'Counter';

		this.dom = dom;
		this.number = this.dom.querySelector<HTMLElement>(`[data-number]`);
		this.from = this.dom.dataset.from;
		this.to = this.dom.dataset.to;
		this.duration = this.dom.dataset.duration;

		this.activeClass = `counter--active`;
	}

	/**
	 * init - Initialize the form
	 */
	init() {
		Logger.info(`${this.NAME} > init`);

		this.setWidth();

		this.initCounter();
	}

	/**
	 * hideCounter - Hide the counter
	 */
	hideCounter() {
		Logger.info(`${this.NAME} > hideCounter`);

		this.dom.classList.remove(this.activeClass);
	}

	/**
	 * initCounter - Initialize the counter
	 */
	initCounter() {
		Logger.info(`${this.NAME} > initCounter`);

		// CountUp's `duration` is in SECONDS, but the theme settings store it in
		// milliseconds (schema default 1000, homepage uses 300). Passing it raw made
		// CountUp run for 300s/1000s — re-rendering the number every frame, which kept
		// the whole third-party stack (AfterShip, Klaviyo, Shopify Web Pixels) awake
		// every frame. Convert ms -> s so the intro count finishes quickly, then hand
		// off to a low-frequency live ticker (see startLiveTicker).
		if (!this.number) return;

		var countUp = new CountUp(this.number, Number(this.to), {
			duration: Number(this.duration) / 1000,
			separator: ',',
			decimal: '.',
			decimalPlaces: 0,
			prefix: '',
			startVal: Number(this.from),
			enableScrollSpy: true,

		});
		countUp.start(() => this.startLiveTicker());

		this.showCounter();
	}

	/**
	 * startLiveTicker - Keep the number climbing forever after the intro count.
	 *
	 * The increments past the loaded value are simulated (the source number is
	 * fixed at page load) — this is a "live sold" social-proof effect.
	 *
	 * Deliberately updates only a few times per SECOND, not per frame. A per-frame
	 * counter is exactly what used to peg the main thread and wake the analytics
	 * MutationObservers every frame; at ~3 text updates/sec the page stays idle
	 * while still looking live.
	 */
	startLiveTicker() {
		if (this.tickerStarted) return;
		this.tickerStarted = true;

		const INTERVAL_MS = 300;
		const INCREMENT = 1;
		let value = Number(this.to);

		const number = this.number;
		if (!number) return;

		setInterval(() => {
			value += INCREMENT;
			number.textContent = value.toLocaleString('en-US');
		}, INTERVAL_MS);
	}

	/**
	 * setWidth - Set the min-width of the counter to be 20px wider than the number
	 */
	setWidth() {
		Logger.info(`${this.NAME} > setWidth`);

		this.dom.style.minWidth = `${this.dom.offsetWidth + 20}px`;
	}

	/**
	 * showCounter - Show the counter
	 */
	showCounter() {
		Logger.info(`${this.NAME} > showCounter`);

		this.dom.classList.add(this.activeClass);
	}

}

Object.assign(Counter.prototype, event);

export default Counter;
